import {
  Alert,
  Button,
  ButtonGroup,
  EditableText,
  Intent,
  NonIdealState,
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import React, { useContext, useState } from "react";
import { useParams } from "react-router";
import { Link, useNavigate } from "react-router-dom";
import { Page } from "../../utils/Layout/Layout";
import ListComponent from "../../utils/ListComponent/ListComponent";
import StatusBar from "../../utils/StatusBar/StatusBar";
import TextEdit from "../../utils/TextEdit/TextEdit";

import { TorgaContext } from '../TorgaContext';
import { ObjectTypes, urlToActivityID, urlToReview } from '../TorgaRouter';
import Fields from './Fields';
import { actionActivityCreate, actionActivityUpdate } from './actions';
import './activity.css';
import GoalList from '../Goal/GoalList';
import { actionActivityEquipmentSet } from '../Equipment/actions';

export default function Activity() {
  const { state, dispatch, dispatchAction } = useContext(TorgaContext);
  const [alertOpen, setAlertOpen] = useState(false);
  const [selectedEquipmentID, setSelectedEquipmentID] = useState('');
  let { id: prefixedID } = useParams();
  const id = prefixedID?.slice(1);
  const { [id]: activity } = state.activities.byID;
  const navigate = useNavigate();

  if (!id) {
    return (
      <NonIdealState
        icon={IconNames.PULSE}
        description="Activities cover 360° of your life, from work to play. Choose one to get started. This helps make sure you don't miss any of your important activities."
      />
    );
  }

  if (!activity) {
    return <div>Loading...</div>;
  }

  const { [activity.parent_id]: parent } = state.activities.byID;
  const { [activity.id]: childrenIDs } = state.activities.idsBy.parentID;
  const activities = childrenIDs?.map(
    (activityID) => state.activities.byID[activityID]
  );
  const goals =
    state.goals.idsBy.activityID[id]?.map((id) => state.goals.byID[id]) || [];
  const linkedEquipmentIDs = state.equipments.idsBy.activityID[id] || [];
  const linkedEquipments = linkedEquipmentIDs
    .map((equipmentID) => state.equipments.byID[equipmentID])
    .filter(Boolean);
  const availableEquipments = state.equipments.allIDs
    .map((equipmentID) => state.equipments.byID[equipmentID])
    .filter(Boolean)
    .filter((equipment) => !equipment.deleted)
    .filter((equipment) => !linkedEquipmentIDs.includes(equipment.id));

  function handleChange(field, value) {
    dispatch({ type: 'ACTIVITY_CHANGE', payload: { id, field, value } });
  }

  function handleConfirm(field, value) {
    dispatchAction(actionActivityUpdate(id, field, value));
  }

  return (
    <Page>
      <h1>
        {' '}
        A{activity.id}
        {' - '}
        <div style={{ float: 'right', textAlign: 'right' }}>
          <EditableText
            placeholder="Icon"
            value={activity.icon}
            onChange={(e) => handleChange('icon', e)}
            onConfirm={(e) => handleConfirm('icon', e)}
            selectAllOnFocus={true}
          />
        </div>
        <EditableText
          placeholder="Activity Name"
          value={activity.name}
          onChange={(e) => handleChange('name', e)}
          onConfirm={(e) => handleConfirm('name', e)}
          selectAllOnFocus={true}
        />
      </h1>
      <StatusBar
        status={activity.status}
        onChange={(status) => handleConfirm('status', status)}
      />{' '}
      <ButtonGroup>
        <Button
          text="Move"
          draggable={true}
          onDragStart={(e) => {
            e.dataTransfer.setData('text/plain', id);
            e.dataTransfer.dropEffect = 'move';
          }}
        />
      </ButtonGroup>{' '}
      <ButtonGroup>
        <Button
          onClick={() => {
            handleConfirm('next_review', new Date());
            navigate(urlToReview());
          }}
        >
          Reviewed
        </Button>
      </ButtonGroup>{' '}
      <Button icon={IconNames.TRASH} onClick={() => setAlertOpen(true)}>
        Delete
      </Button>
      <hr />
      <Fields
        object={activity}
        handleConfirm={(field, value) => {
          dispatchAction(actionActivityUpdate(id, field, value));
        }}
      />
      <table>
        <tbody>
          {parent !== undefined && (
            <tr>
              <td>Parent</td>
              <td>
                <Link to={urlToActivityID(activity.parent_id)}>
                  {parent.name}
                </Link>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <hr />
      <GoalList activityID={activity.id} />
      <hr />
      <h3>Equipments</h3>
      {linkedEquipments.length === 0 ? (
        <div>No equipment linked.</div>
      ) : (
        <ul>
          {linkedEquipments.map((equipment) => (
            <li key={equipment.id}>
              E{equipment.id} - {equipment.name || '(unnamed equipment)'}{' '}
              <Button
                minimal
                icon={IconNames.REMOVE}
                onClick={() =>
                  dispatchAction(
                    actionActivityEquipmentSet(activity.id, equipment.id, true)
                  )
                }
              />
            </li>
          ))}
        </ul>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select
          value={selectedEquipmentID}
          onChange={(event) => setSelectedEquipmentID(event.target.value)}
        >
          <option value="">Select equipment...</option>
          {availableEquipments.map((equipment) => (
            <option key={equipment.id} value={equipment.id}>
              E{equipment.id} - {equipment.name || '(unnamed equipment)'}
            </option>
          ))}
        </select>
        <Button
          disabled={!selectedEquipmentID}
          icon={IconNames.ADD}
          onClick={() => {
            dispatchAction(
              actionActivityEquipmentSet(activity.id, selectedEquipmentID, false)
            );
            setSelectedEquipmentID('');
          }}
        >
          Link equipment
        </Button>
      </div>
      <Alert
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        icon="trash"
        intent={Intent.DANGER}
        isOpen={alertOpen}
        canEscapeKeyCancel={true}
        onCancel={() => setAlertOpen(false)}
        onConfirm={() =>
          dispatchAction(
            actionActivityUpdate(id, 'deleted', true, () => {
              setAlertOpen(false);
            })
          )
        }
      >
        <p> Delete activity ? </p>
      </Alert>
      <ListComponent
        objectType={ObjectTypes.ACTIVITIES}
        values={activities}
        render={(activity) => [activity.name]}
        quickentry={{ placeholder: 'Add new Activity' }}
        onCreate={(name) =>
          dispatchAction(actionActivityCreate(activity.id, name))
        }
      />
    </Page>
  );
}
